import classNames from 'classnames';
import first from 'lodash/first';
import uniqueId from 'lodash/uniqueId';
import * as React from 'react';
import type { CardsSectionColorVariant } from '../CardsSectionColorVariant';
import type { CardProps } from '~/Partials/Card/Card';
import { Card } from '~/Partials/Card/Card';
import { List } from '~/Partials/List/List';
import type { ScrollableTabsTranslations } from '~/Partials/Tabs/StyledTabs';
import { StyledTabs } from '~/Partials/Tabs/StyledTabs';
import type { TabsItemProps } from '~/Partials/Tabs/Tabs';
import styles from './CardsSectionTabs.module.css';

export interface CardsSectionTabContent {
    heading: string;
    items: Omit<CardProps, 'colorVariant'>[];
}

export interface CardsSectionTabsProps {
    label: string;
    tabContents: CardsSectionTabContent[];
    translations: ScrollableTabsTranslations;
    colorVariant?: CardsSectionColorVariant;
    listWrapperClassName?: string;
}

export const CardsSectionTabs: React.FC<CardsSectionTabsProps> = props => {
    const { label, tabContents, translations, listWrapperClassName, colorVariant = 'light' } = props;

    const tabs: TabsItemProps[] = tabContents.map(content => ({
        tabKey: uniqueId('tab__'),
        tabName: content.heading,
        tabContent: (
            <List
                className={styles.TabContent}
                items={content.items}
                renderItem={item => (
                    <li key={uniqueId('tabItem__')}>
                        <Card
                            className={styles.Card}
                            contentClassName={styles.CardContent}
                            {...item}
                            colorVariant={colorVariant}
                        />
                    </li>
                )}
            />
        ),
    }));

    const firstItem = first(tabs);

    if (!firstItem) {
        throw new Error('At least one tab has to be present');
    }

    return (
        <StyledTabs
            translations={translations}
            tabsListClassName={styles.TabList}
            selectedTab={firstItem.tabKey}
            tabs={tabs}
            colorVariant={colorVariant}
            name={label}
            TabListWrapper={tabListWrapperProps => (
                <div className={classNames(styles.ListWrapper, listWrapperClassName)}>
                    {tabListWrapperProps.children}
                </div>
            )}
        />
    );
};
